import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAppSelector } from '../hooks/redux';
import WelcomeBanner from '../components/dashboard/WelcomeBanner';
import QuickActions from '../components/dashboard/QuickActions';
import AnalyticsWidget from '../components/dashboard/AnalyticsWidget';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import ProjectManagement from '../components/dashboard/ProjectManagement';
import OpportunityRecommendations from '../components/dashboard/OpportunityRecommendations';
import { dashboardAPI } from '../services/api';
import RecentPortfolioUpdates from '../components/dashboard/RecentPortfolioUpdates';
import CollaborationRequestsWidget from '../components/dashboard/CollaborationRequestsWidget';
import WeeklyGoalWidget from '../components/dashboard/WeeklyGoalWidget';
// import WelcomeModal from '../components/dashboard/WelcomeModal';
// import ReferralQualificationModal from '../components/dashboard/ReferralQualificationModal';

const DashboardPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [dashboardData, setDashboardData] = useState({
    today_stats: {
      upcoming_events: 0,
      pending_messages: 0,
      new_opportunities: 0
    },
    analytics: {
      profile_views: { current: 0, change: 0 },
      collaboration_requests: { current: 0, change: 0 },
      opportunity_applications: { current: 0, change: 0 },
      new_followers: { current: 0, change: 0 },
      search_appearances: { current: 0, change: 0 }
    },
    activities: [],
    active_projects: [],
    recommendations: [],
    portfolio_updates: []
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getDashboardData();
      setDashboardData(prev => ({
        ...prev,
        ...response.data.data.data
      }));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-48 bg-gray-100 dark:bg-gray-800 rounded-2xl"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="h-96 bg-gray-100 dark:bg-gray-800 rounded-2xl"></div>
          <div className="h-96 bg-gray-100 dark:bg-gray-800 rounded-2xl"></div>
          <div className="h-96 bg-gray-100 dark:bg-gray-800 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="space-y-4 md:space-y-8">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <WelcomeBanner 
          user={user}
          today_stats={dashboardData.today_stats}
        />
    </motion.div>
{/* 
      <WelcomeModal />
      <ReferralQualificationModal /> */}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-8">
        
        {/* Left Column - Quick Actions & Analytics */}
        <div className="lg:col-span-8 space-y-4 md:space-y-8">
          <QuickActions userType={user.user_type} />
          
          {user.user_type !== 'hirer' && dashboardData.portfolio_updates && (
            <RecentPortfolioUpdates 
              portfolioUpdates={dashboardData.portfolio_updates}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
             {/* Activity Feed */}
             <ActivityFeed activities={dashboardData.activities} />
             
             {/* Projects or Requests */}
             <div className="space-y-4 md:space-y-8">
               {user.user_type !== 'hirer' ? (
                 <>
                   <CollaborationRequestsWidget />
                   <ProjectManagement 
                     projects={dashboardData.active_projects}
                     userType={user.user_type}
                   />
                 </>
               ) : (
                 <CollaborationRequestsWidget onlySent={true} />
               )}
             </div>
          </div>
        </div>

        {/* Right Column - Recommendations & Analytics (Sticky on Desktop) */}
        <div className="lg:col-span-4 space-y-4 md:space-y-8">
          {user.user_type !== 'hirer' && (
            <AnalyticsWidget 
              data={dashboardData.analytics}
              userType={user.user_type}
            />
          )}

          {user.user_type !== 'hirer' && (
             <OpportunityRecommendations 
               recommendations={dashboardData.recommendations}
               userType={user.user_type}
             />
          )}

          {user.user_type !== 'hirer' && (
            <WeeklyGoalWidget 
              current={dashboardData.analytics.collaboration_requests.current}
            />
          )}

          {/* Hirer-specific widgets */}
          {user.user_type === 'hirer' && (
            <>
              {/* Opportunity Recommendations for Hirers
              <OpportunityRecommendations 
                recommendations={dashboardData.recommendations}
                userType={user.user_type}
              /> */}

              {/* Explore Tips */}
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-2xl p-6 border border-teal-100 dark:border-teal-800/30">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">💡 Tips for Hirers</h3>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 dark:text-teal-400">•</span>
                    <span>Browse creators and models in the <strong>Explore</strong> page</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 dark:text-teal-400">•</span>
                    <span>Send collaboration requests to work with professionals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 dark:text-teal-400">•</span>
                    <span>Filter by location, budget, and specialization</span>
                  </li>
                  {/* <li className="flex items-start gap-2">
                    <span className="text-teal-600 dark:text-teal-400">•</span>
                    <span>Upgrade to Creator to post opportunities</span>
                  </li> */}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
